import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  MRT_EditActionButtons as MrtEditActionButtons,
  MRT_TableOptions,
} from "material-react-table";
import moment from "moment/moment";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

interface IDataTableProps {
  columns: any[];
  data: any[];
  onCreate?: (values: any) => void;
  onEditSave?: (values: any) => void;
  onDelete?: (values: any) => void;
  valueKey: string;
  labelKey: string;
  typeKey?: string;
  enableColumnActions?: boolean;
  enableColumnFilters?: boolean;
  enablePagination?: boolean;
  enableSorting?: boolean;
  enableEditing?: boolean;
  enableHiding?: boolean;
  enableDensityToggle?: boolean;
  createDisplayMode?: "row" | "modal" | "custom";
  editDisplayMode?: "row" | "modal" | "custom";
  state?: any;
  recordIdKey: string;
  hideEditFields?: string[];
  selectionOption?: any;
  createRecordTitle?: string;
}

const DataTable = ({
  columns = [],
  data = [],
  onCreate,
  onEditSave,
  onDelete,
  valueKey,
  labelKey,
  typeKey = "",
  enableColumnActions = false,
  enableColumnFilters = false,
  enablePagination = false,
  enableSorting = false,
  enableEditing = false,
  enableHiding = false,
  enableDensityToggle = false,
  createDisplayMode = "row",
  editDisplayMode = "row",
  state = {},
  recordIdKey = "",
  hideEditFields = [],
  selectionOption = null,
  createRecordTitle = "Create New Record",
}: IDataTableProps) => {
  const [validationErrors, setValidationErrors] = useState<any>({});

  const handleCreateUser: MRT_TableOptions<any>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    // setLoading(true);
    if (onCreate) {
      onCreate(values);
    }
    table.setCreatingRow(null); //exit creating mode
  };

  const handleSaveUser: MRT_TableOptions<any>["onEditingRowSave"] = async ({
    values,
    table,
  }) => {
    if (onEditSave) {
      onEditSave(values);
    }
    table.setEditingRow(null);
  };

  const renderCreateRowDialogContent = ({
    table,
    row,
    internalEditComponents,
  }: any) => (
    <>
      <DialogTitle variant="h3">Create New User</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        {internalEditComponents} {/* or render custom edit components here */}
      </DialogContent>
      <DialogActions>
        <MrtEditActionButtons variant="text" table={table} row={row} />
      </DialogActions>
    </>
  );

  const renderRowActions = ({ row, table }: any) => (
    <Box sx={{ display: "flex", gap: "1rem" }}>
      <Tooltip title="Edit">
        <IconButton onClick={() => table.setEditingRow(row)}>
          <EditIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const openDeleteConfirmModal = (row: any) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      if (onDelete) {
        onDelete(row.original.id);
      }
    }
  };

  const renderTopToolbarCustomActions = ({ table }: any) => (
    <Button
      variant="contained"
      onClick={() => {
        table.setCreatingRow(true);
      }}
    >
      {createRecordTitle}
    </Button>
  );

  const tableColumns = useMemo(
    () =>
      columns
        ? columns.map((item: any) => {
            const col: any = {
              accessorKey: item[valueKey],
              header: item[labelKey],
              type: item[typeKey],
              size: 120,
              enableEditing: !hideEditFields?.includes(item.column_name),
              Cell: ({ cell }: any) => {
                return (
                  <Box component="span">
                    {renderValue(item.column_type, cell.getValue())}
                  </Box>
                );
              },
            };

            if (hideEditFields?.includes(item.column_name)) {
              col.Edit = ({ cell, column, row, table }: any) => null;
            } else {
              col.editSelectOptions = [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ];
              col.muiEditTextFieldProps = {
                type:
                  item.column_type === "Boolean"
                    ? "select"
                    : item.column_type === "Date"
                    ? "Date"
                    : "text",
                required: true,
                select: item.column_type === "Boolean",
                error: !!validationErrors[item?.column_name],
                helperText: validationErrors[item?.column_name],
                onFocus: () =>
                  setValidationErrors({
                    ...validationErrors,
                    [item?.column_name]: undefined,
                  }),
              };
            }
            return col;
          })
        : [], // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, validationErrors]
  );

  const renderValue = (colType: string, colValue: string) => {
    switch (colType) {
      case "Boolean":
        return colValue ? "Yes" : "No";
      case "Date":
        return !colValue
          ? "-"
          : colValue === "None"
          ? "-"
          : moment(colValue).format("YYYY-MM-DD");
      case "String":
        return colValue || "-";
      case "Number":
        return colValue || "-";
    }
  };

  return (
    <MaterialReactTable
      columns={tableColumns}
      data={data}
      enableColumnActions={enableColumnActions}
      enableColumnFilters={enableColumnFilters}
      enablePagination={enablePagination}
      enableSorting={enableSorting}
      createDisplayMode={createDisplayMode}
      editDisplayMode={editDisplayMode}
      enableEditing={enableEditing}
      enableHiding={enableHiding}
      enableDensityToggle={enableDensityToggle}
      getRowId={(row: any) => row[recordIdKey]}
      onCreatingRowSave={handleCreateUser}
      onEditingRowSave={handleSaveUser}
      state={state}
      renderCreateRowDialogContent={renderCreateRowDialogContent}
      renderRowActions={renderRowActions}
      renderTopToolbarCustomActions={renderTopToolbarCustomActions}
      onCreatingRowCancel={() => setValidationErrors({})}
      onEditingRowCancel={() => setValidationErrors({})}
    />
  );
};

export default DataTable;
